import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import BluebookCalculatorIcon from '../../common/icons/bluebook-calculator-icon.tsx';
import BluebookReferenceIcon from '../../common/icons/bluebook-reference-icon.tsx';
import MarkerLightDownIcon from '../../../assets/icons/marker-light-down.svg?react';
import PinnedNoteIcon from '../../../assets/icons/pinned-note.svg?react';
import { useHighlight } from '../../../contexts/highlight-provider/useHighlight.ts';
import { cn } from '../../../utils/common-utils.ts';

interface IProps {
  isMath: boolean;
  isCalc: boolean;
  onToggleCalc: () => void;
  showReference?: boolean;
  isReferenceOpen?: boolean;
  onOpenReference?: () => void;
  showReport?: boolean;
  onOpenReport?: () => void;
}

const reviewToolButtonClassName =
  'relative mx-0.5 flex h-[4rem] w-[4.4rem] flex-col items-center justify-center rounded-md border border-transparent bg-transparent px-1.5 text-[#222] transition-colors hover:bg-[rgba(254,219,0,0.18)] focus:border-none focus:outline-none';

const toolButtonIconRowClassName = 'flex h-7 items-center justify-center';

const toolButtonLabelClassName =
  'relative mt-1.5 inline-flex whitespace-nowrap text-xs font-semibold leading-none';

const ActiveLabelUnderline = () => (
  <span
    aria-hidden
    className="pointer-events-none absolute -bottom-[5px] left-0 right-0 h-0.5 rounded-full bg-[#222]"
  />
);

const ToolButtonLabel = ({
  isActive = false,
  label,
}: {
  isActive?: boolean;
  label: string;
}) => (
  <div className={toolButtonLabelClassName}>
    <span>{label}</span>
    {isActive && <ActiveLabelUnderline />}
  </div>
);

const ReferenceToolbarIcon = () => (
  <BluebookReferenceIcon className="translate-y-px" />
);

export const ReviewTools = ({
  isMath,
  isCalc,
  onToggleCalc,
  showReference = false,
  isReferenceOpen = false,
  onOpenReference,
  showReport = false,
  onOpenReport,
}: IProps) => {
  const {
    highlightToggle,
    setHighlightToggle,
  } = useHighlight();

  return (
    <div className="flex flex-wrap items-center justify-end gap-0.5">
      {showReport && onOpenReport && (
        <button
          type="button"
          className={reviewToolButtonClassName}
          onClick={onOpenReport}
        >
          <div className={toolButtonIconRowClassName}>
            <ExclamationTriangleIcon className="h-7 w-7" />
          </div>
          <ToolButtonLabel label="Report" />
        </button>
      )}

      {!isMath && (
        <button
          type="button"
          className={cn(
            reviewToolButtonClassName,
            'w-[6.9rem]',
          )}
          onClick={() => setHighlightToggle((prevState) => !prevState)}
        >
          <div className={cn(toolButtonIconRowClassName, 'gap-1')}>
            <MarkerLightDownIcon
              width={18}
              height={18}
              fill={'currentColor'}
            />
            <PinnedNoteIcon
              width={18}
              height={18}
              fill={'currentColor'}
            />
          </div>
          <ToolButtonLabel
            isActive={highlightToggle}
            label="Highlights & Notes"
          />
        </button>
      )}

      {isMath && (
        <button
          type="button"
          className={cn(
            reviewToolButtonClassName,
            'w-[5.3rem]',
          )}
          onClick={onToggleCalc}
        >
          <div className={toolButtonIconRowClassName}>
            <BluebookCalculatorIcon className="h-[1.4rem] w-[1.4rem]" />
          </div>
          <ToolButtonLabel isActive={isCalc} label="Calculator" />
        </button>
      )}

      {isMath && showReference && onOpenReference && (
        <button
          type="button"
          className={reviewToolButtonClassName}
          onClick={onOpenReference}
        >
          <div className={toolButtonIconRowClassName}>
            <ReferenceToolbarIcon />
          </div>
          <ToolButtonLabel isActive={isReferenceOpen} label="Reference" />
        </button>
      )}
    </div>
  );
};
